<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDevice } from '@/composables/useInjectValues'
import { sendOutputReportFactory } from '@/utils/dualsense/ds.util'
import { createAsyncLock } from '@/utils/lock.util'
import DouSelect from '@/components/base/DouSelect.vue'
import DouButton from '@/components/base/DouButton.vue'
import DouSwitch from '@/components/base/DouSwitch.vue'

const { t } = useI18n()
const device = useDevice()



// HID模式相关
const hidMode = ref('0')
const showCustomFields = computed(() => hidMode.value === '2')

const hidModeSets = computed(() => {
  return [
    {
      value: '0',
      label: 'DS5 手柄模式',
    },
    {
      value: '1',
      label: '鼠标模式 (G502)',
    },
    {
      value: '2',
      label: '自定义模式',
    },
  ]
})

// 自定义HID信息
const customVid = ref('046D')
const customPid = ref('C08B')
const customBcd = ref('0016')
const customSerial = ref('000000000000')
const customMfr = ref('Logitech')
const customProd = ref('G502 LIGHTSPEED Wireless Gaming Mouse')
const customIdx4 = ref('G502 Wireless')

// HID功能开关
const ds5Enable = ref(true)
const keyboardEnable = ref(true)
const mouseEnable = ref(true)
const touchEnable = ref(true)

// WiFi配置相关
const wifiSsid = ref('')
const wifiPassword = ref('')
const udpPort = ref('')

// 发送逻辑
const _sendOutputReport = computed(() => {
  return sendOutputReportFactory(device.value)
})

const outputReportLock = createAsyncLock()

// 0x55AA 协议帧构建
const USB_REPORT_SIZE = 47
const USB_MAX_INNER = USB_REPORT_SIZE - 2 // 45

function buildProtoFrame(data: Uint8Array) {
  const len = data.length
  const frame = new Uint8Array(4 + len)
  frame[0] = 0x55
  frame[1] = 0xAA
  frame[2] = len
  frame.set(data, 3)
  let cs = len
  for (let i = 0; i < len; i++) cs ^= data[i]
  frame[3 + len] = cs
  return frame
}

async function sendPayload(reportId: number, payload: Uint8Array, label: string) {
  if (!device.value) {
    addLog('错误', '设备未连接')
    return
  }

  const protoFrame = buildProtoFrame(payload)
  const totalChunks = Math.ceil(protoFrame.length / USB_MAX_INNER)

  for (let i = 0; i < totalChunks; i++) {
    const offset = i * USB_MAX_INNER
    const chunkLen = Math.min(USB_MAX_INNER, protoFrame.length - offset)
    const packet = new Uint8Array(USB_REPORT_SIZE)
    packet[0] = chunkLen
    packet.set(protoFrame.subarray(offset, offset + chunkLen), 1)

    try {
      await outputReportLock(async () => {
        await device.value.device.sendReport(reportId, packet)
      })
    } catch (err) {
      addLog('发送失败', `chunk ${i + 1}/${totalChunks}: ${err}`)
      console.error('sendReport error:', err)
      return
    }
  }


}

// 功能函数
async function sendHidMode() {
  const mode = parseInt(hidMode.value)
  const payload = new Uint8Array([0xFE, 0x00, mode])
  await sendPayload(2, payload, 'HID模式')
}

async function sendHidEnable(ds5_enable: boolean, keyboard_enable: boolean, mouse_enable: boolean, touch_enable: boolean) {
  let flag = 0x00
  if (ds5_enable)
    flag |= 0x01
  if (keyboard_enable)
    flag |= 0x02
  if (mouse_enable)
    flag |= 0x04
  if (touch_enable)
    flag |= 0x08
  const payload = new Uint8Array([0xFE, 0x02, flag])
  await sendPayload(2, payload, 'HID功能开关')
}

async function sendCustomDesc() {
  const vidRaw = customVid.value.trim()
  const pidRaw = customPid.value.trim()
  const bcdRaw = customBcd.value.trim()
  const mfr = customMfr.value.trim()
  const prod = customProd.value.trim()
  const serial = customSerial.value.trim()
  const idx4 = customIdx4.value.trim()

  // 空值用 0xFFFF 表示"不更新"，固件侧跳过
  const vidVal = vidRaw ? (parseInt(vidRaw.replace(/^0x/i, ''), 16) || 0xFFFF) : 0xFFFF
  const pidVal = pidRaw ? (parseInt(pidRaw.replace(/^0x/i, ''), 16) || 0xFFFF) : 0xFFFF
  const bcdVal = bcdRaw ? (parseInt(bcdRaw.replace(/^0x/i, ''), 16) || 0xFFFF) : 0xFFFF

  // 命令格式: [0xFE, 0x01, vid(2B), pid(2B), bcd(2B), mfr_len, mfr..., prod_len, prod..., serial_len, serial..., idx4_len, idx4...]
  const encoder = new TextEncoder()
  const mfrBytes = mfr ? encoder.encode(mfr) : new Uint8Array(0)
  const prodBytes = prod ? encoder.encode(prod) : new Uint8Array(0)
  const serialBytes = serial ? encoder.encode(serial) : new Uint8Array(0)
  const idx4Bytes = idx4 ? encoder.encode(idx4) : new Uint8Array(0)

  const totalLen = 2 + 1 + 2 + 2 + 2 + 1 + mfrBytes.length + 1 + prodBytes.length + 1 + serialBytes.length + 1 + idx4Bytes.length
  const payload = new Uint8Array(totalLen)
  let offset = 0

  // 命令头
  payload[offset++] = 0xFE // CMD_HID_CONFIG
  payload[offset++] = 0x01 // sub_cmd: set custom desc

  // VID (2B little-endian), 0xFFFF = 不更新
  payload[offset++] = vidVal & 0xFF
  payload[offset++] = (vidVal >> 8) & 0xFF

  // PID (2B little-endian), 0xFFFF = 不更新
  payload[offset++] = pidVal & 0xFF
  payload[offset++] = (pidVal >> 8) & 0xFF

  // bcdDevice (2B little-endian), 0xFFFF = 不更新
  payload[offset++] = bcdVal & 0xFF
  payload[offset++] = (bcdVal >> 8) & 0xFF

  // 字符串字段: [len, bytes...]，len=0 表示不更新
  payload[offset++] = Math.min(mfrBytes.length, 63)
  payload.set(mfrBytes.subarray(0, 63), offset)
  offset += Math.min(mfrBytes.length, 63)

  payload[offset++] = Math.min(prodBytes.length, 127)
  payload.set(prodBytes.subarray(0, 127), offset)
  offset += Math.min(prodBytes.length, 127)

  payload[offset++] = Math.min(serialBytes.length, 31)
  payload.set(serialBytes.subarray(0, 31), offset)
  offset += Math.min(serialBytes.length, 31)

  payload[offset++] = Math.min(idx4Bytes.length, 63)
  payload.set(idx4Bytes.subarray(0, 63), offset)
  offset += Math.min(idx4Bytes.length, 63)

  await sendPayload(2, payload.subarray(0, offset), '自定义HID描述符')
}

async function sendWifi() {
  const encoder = new TextEncoder()
  
  // 发送 SSID 配置
  if (wifiSsid.value) {
    const ssid = wifiSsid.value
    const ssidBytes = encoder.encode(ssid)
    const ssidLen = Math.min(ssidBytes.length, 32)
    const ssidPayload = new Uint8Array(3 + ssidLen)
    ssidPayload[0] = 0xFF
    ssidPayload[1] = 0x00 // SSID 配置命令
    ssidPayload[2] = ssidLen
    ssidPayload.set(ssidBytes.subarray(0, ssidLen), 3)
    await sendPayload(2, ssidPayload, 'WiFi SSID配置')
  }
  
  // 发送密码配置
  if (wifiPassword.value) {
    const password = wifiPassword.value
    const passBytes = encoder.encode(password)
    const passLen = Math.min(passBytes.length, 64)
    const passPayload = new Uint8Array(3 + passLen)
    passPayload[0] = 0xFF
    passPayload[1] = 0x01 // 密码配置命令
    passPayload[2] = passLen
    passPayload.set(passBytes.subarray(0, passLen), 3)
    await sendPayload(2, passPayload, 'WiFi 密码配置')
  }
  
  // 发送 UDP 端口配置
  if (udpPort.value) {
    const port = udpPort.value
    const portBytes = encoder.encode(port)
    const portLen = Math.min(portBytes.length, 6)
    const portPayload = new Uint8Array(3 + portLen)
    portPayload[0] = 0xFF
    portPayload[1] = 0x02 // UDP 端口配置命令
    portPayload[2] = portLen
    portPayload.set(portBytes.subarray(0, portLen), 3)
    await sendPayload(2, portPayload, 'UDP 端口配置')
  }
}

async function sendReboot() {
  const payload = new Uint8Array([0xFD])
  await sendPayload(2, payload, '重启')
}



onBeforeUnmount(() => {
  // 清理逻辑
})
</script>

<template>
  <table>
    <tbody>


      <!-- HID 模式选择 -->
      <tr>
        <td class="label">
          HID 模式
        </td>
        <td class="value">
          <div>
            <DouSelect v-model="hidMode" :options="hidModeSets" />
          </div>
        </td>
      </tr>
      <tr>
        <td class="label">
          操作
        </td>
        <td class="value">
          <div>
            <DouButton @click="sendHidMode">应用模式</DouButton>
          </div>
        </td>
      </tr>

      <!-- HID 功能开关 -->
      <tr>
        <td class="label">
          HID接口开关
        </td>
        <td class="value">
          <div class="flex-col items-end gap-1">
            <div class="flex items-center justify-between w-full">
              <span class="text-sm">DS5</span>
              <DouSwitch v-model="ds5Enable" />
            </div>
            <div class="flex items-center justify-between w-full">
              <span class="text-sm">键盘</span>
              <DouSwitch v-model="keyboardEnable" />
            </div>
            <div class="flex items-center justify-between w-full">
              <span class="text-sm">鼠标</span>
              <DouSwitch v-model="mouseEnable" />
            </div>
            <div class="flex items-center justify-between w-full">
              <span class="text-sm">触摸</span>
              <DouSwitch v-model="touchEnable" />
            </div>
          </div>
        </td>
      </tr>
      <tr>
        <td class="label">
          操作
        </td>
        <td class="value">
          <div>
            <DouButton @click="sendHidEnable(ds5Enable, keyboardEnable, mouseEnable, touchEnable)">应用开关</DouButton>
          </div>
        </td>
      </tr>

      <!-- 自定义 HID 信息 -->
      <tr v-if="showCustomFields">
        <td class="label">
          Vendor ID (hex)
        </td>
        <td class="value">
          <div>
            <input v-model="customVid" type="text" class="form-input">
          </div>
        </td>
      </tr>
      <tr v-if="showCustomFields">
        <td class="label">
          Product ID (hex)
        </td>
        <td class="value">
          <div>
            <input v-model="customPid" type="text" class="form-input">
          </div>
        </td>
      </tr>
      <tr v-if="showCustomFields">
        <td class="label">
          bcdDevice (hex)
        </td>
        <td class="value">
          <div>
            <input v-model="customBcd" type="text" class="form-input">
          </div>
        </td>
      </tr>
      <tr v-if="showCustomFields">
        <td class="label">
          序列号
        </td>
        <td class="value">
          <div>
            <input v-model="customSerial" type="text" class="form-input">
          </div>
        </td>
      </tr>
      <tr v-if="showCustomFields">
        <td class="label">
          制造商
        </td>
        <td class="value">
          <div>
            <input v-model="customMfr" type="text" class="form-input">
          </div>
        </td>
      </tr>
      <tr v-if="showCustomFields">
        <td class="label">
          产品名
        </td>
        <td class="value">
          <div>
            <input v-model="customProd" type="text" class="form-input">
          </div>
        </td>
      </tr>
      <tr v-if="showCustomFields">
        <td class="label">
          索引4字符串
        </td>
        <td class="value">
          <div>
            <input v-model="customIdx4" type="text" class="form-input">
          </div>
        </td>
      </tr>
      <tr v-if="showCustomFields">
        <td class="label">
          操作
        </td>
        <td class="value">
          <div>
            <DouButton @click="sendCustomDesc">写入自定义描述符</DouButton>
          </div>
        </td>
      </tr>

      <!-- WiFi 配置 -->
      <tr>
        <td class="label">
          WiFi SSID
        </td>
        <td class="value">
          <div>
            <input v-model="wifiSsid" type="text" placeholder="SSID" class="form-input">
          </div>
        </td>
      </tr>
      <tr>
        <td class="label">
          WiFi 密码
        </td>
        <td class="value">
          <div>
            <input v-model="wifiPassword" type="text" placeholder="密码" class="form-input">
          </div>
        </td>
      </tr>
      <tr>
        <td class="label">
          UDP 端口
        </td>
        <td class="value">
          <div>
            <input v-model="udpPort" type="text" placeholder="端口" class="form-input">
          </div>
        </td>
      </tr>
      <tr>
        <td class="label">
          操作
        </td>
        <td class="value">
          <div>
            <DouButton @click="sendWifi">发送 WiFi 配置</DouButton>
          </div>
        </td>
      </tr>

      <!-- 重启设备 -->
      <tr>
        <td class="label">
          设备控制
        </td>
        <td class="value">
          <div>
            <DouButton @click="sendReboot">重启设备</DouButton>
          </div>
        </td>
      </tr>



    </tbody>
  </table>
</template>

<style scoped lang="scss">
table {
  @apply w-full;
  border-collapse: separate;
  border-spacing: 0 8px;
}

.label,
:deep(.label) {
  @apply text-primary font-bold whitespace-pre-wrap;
}

.value,
:deep(.value) {
  @apply max-w-50% ps-2;

  &>div {
    @apply flex items-center justify-end;
  }
}

.status-tag {
  @apply inline-block px-3 py-1 rounded-full text-sm font-medium bg-gray-200;
  
  &.connected {
    @apply bg-green-500 text-white;
  }
}

.form-input {
  @apply px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-full bg-transparent;
  border: 1px solid #2f81f7;
  border-radius: 1.5rem;
}

.form-select {
  @apply px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500;
  border: 1px solid #2f81f7;
}

.btn {
  @apply px-4 py-2 rounded-md font-medium cursor-pointer transition-opacity bg-blue-600 text-white;
  
  &:hover {
    @apply opacity-90;
  }
}

.btn-sm {
  @apply px-2 py-1 rounded-md font-medium cursor-pointer transition-opacity bg-blue-600 text-white text-sm;
  
  &:hover {
    @apply opacity-90;
  }
}

.log-container {
  @apply bg-gray-900 text-gray-200 p-3 rounded-md max-h-60 overflow-y-auto font-mono text-sm w-full;
}

.log-item {
  @apply py-1 border-b border-gray-800;
  
  &:last-child {
    @apply border-b-0;
  }
}
</style>